const AbstractItem = require('@/lib/Items/AbstractItem');
const ItemType = require('@/lib/Items/ItemType');
const SessionEvent = require('@/lib/Sessions/SessionEvent');
const { populate } = require('@/functions/objects');
const {
    toBool,
    toInt,
    toIntArray,
    toNullableInt,
    toString,
} = require('@/functions/transform');

class Ticket extends AbstractItem {
    constructor(data = {}) {
        super();

        /**
         * @type {SessionEvent}
         */
        this.event = null;

        /**
         * @type {number|null}
         */
        this.eventCatID = null;

        this.itemType = ItemType.TICKET;

        /**
         * @type {number}
         */
        this.people = 0;

        this.timeSpecific = '';

        /**
         * Should this ticket come out of the printer when clicking the 'Print Tickets' button?
         * (Badly named field - should be something like "shouldPrintTicket".)
         * This is marked private because we want to rename this to something more sensible. Use the shouldPrintTicket
         * getter instead.
         *
         * @private
         * @type {boolean}
         */
        this.printTicketPerInstance = false;

        /**
         * @type {?Number}
         */
        this.validityPeriod = null;

        /**
         * This value is only populated if we load the waiver definitions for the company while syncing.
         *
         * @type {boolean|null}
         */
        this.hasWaiver = null;

        /**
         * If this ticket has a seating chart, this will be the ID of the seating chart.
         * Note that only event-based tickets can have seating charts.
         *
         * @type {?Number}
         */
        this.seatingChartID = null;

        /**
         * @type {?Number[]}
         */
        this.bookableResourceTypeIDs = null;

        populate(this, data);
    }

    /**
     * @return {number}
     */
    getPeople() {
        return parseInt(this.people ? this.people : 1, 10);
    }

    /**
     * @returns {boolean}
     * @todo Will need to be updated to handle multi-session tickets.
     */
    isSessionBased() {
        return this.timeSpecific === 'Event';
    }

    /**
     * @returns {object}
     */
    getHydrationMap() {
        return Object.assign(
            super.getHydrationMap(),
            {
                bookableResourceTypeIDs: toIntArray,
                event: {
                    field: ['event', 'events'],
                    model: SessionEvent,
                },
                eventCatID: toNullableInt,
                people: toInt,
                printTicketPerInstance: toBool,
                seatingChartID: toNullableInt,
                timeSpecific: toString,
                validityPeriod: toNullableInt,
            }
        );
    }

    /**
     * @return {number|null}
     */
    get eventID() {
        if (this.eventCatID) {
            return this.eventCatID;
        }

        if (this.event?.ID) {
            return this.event.ID;
        }

        return null;
    }

    /**
     * Should this ticket come out of the printer when clicking 'Print Tickets'?
     *
     * @return {boolean}
     */
    get canPrintTicket() {
        return !!this.printTicketPerInstance;
    }

    /**
     * @return {boolean}
     */
    get hasSeats() {
        return !!this.seatingChartID;
    }

    /**
     * @return {boolean}
     */
    get hasBookableResources() {
        return !!(this.bookableResourceTypeIDs && this.bookableResourceTypeIDs.length > 0);
    }
}

module.exports = Ticket;
